
import { Component, Prop, Vue, Mixins } from 'vue-property-decorator';
import { Bar } from 'vue-chartjs';
import { ChartConfig } from './chartConfig';
import { Data, Series, SeriesConfig, SeriesOrderConfiguration } from '../../models';
import ColourService from '@/shared/services/color';
import { ChartDataFormat } from '@/features/reports/models/chartDataFormat';

// @ts-ignore
import * as stacked from 'chartjs-plugin-stacked100';

interface PercentageChartData extends Chart.ChartData {
	originalData: [];
	calculatedData: [];
}

@Component
export default class PercentageStackedBarChart extends Mixins(Bar) {
	@Prop() public data: Data;
	@Prop() public reportId: string;
	@Prop() public seriesOrder: SeriesOrderConfiguration;
	@Prop() public seriesToCompareTo: string;
	public renderSeriesInReverse: boolean = true;

	public mounted() {
		this.addPlugin(stacked);

		const data = this.mapData(this.data);
		const options = this.overrideOptions(ChartConfig.gridChartsGlobalOptions);
		this.renderChart(data, options);
	}

	public overrideOptions(options: Chart.ChartOptions): Chart.ChartOptions {
		options.plugins!.stacked100 = { enable: true, replaceTooltipLabel: false };
		options.plugins!.inlineLabels = {
			reportId: this.reportId,
			reverseLabels: this.renderSeriesInReverse,
			dataFormat: ChartDataFormat.PERCENTAGE,
			defaultLabelValues: this.seriesToCompareTo ? this.calculatePercentageLabels() : undefined
		};
		options.plugins!.crosshair = false;
		options.scales = {
			xAxes: [
				{
					scaleLabel: {
						labelString: this.data.scale.title
					},
					ticks: {
						maxTicksLimit: 32
					},
					gridLines: {
						display: false
					}
				}
			],
			yAxes: [
				{
					ticks: {
						callback: value => `${value}%`
					}
				}
			]
		};
		options.tooltips!.callbacks = {
			label: (tooltipItem: Chart.ChartTooltipItem, data: PercentageChartData) => {
				const index = tooltipItem.index!;
				const datasetIndex = tooltipItem.datasetIndex!;

				const originalValue = this.orderedSeries[datasetIndex].data[index];
				const compareToValue = this.seriesToCompareTo
					? this.getSeries(this.seriesToCompareTo).data[index]
					: this.orderedSeries.reduce((prev, curr) => prev + curr.data[index], 0);

				const percentageOfTotal = compareToValue === 0 ? 0 : (originalValue / compareToValue) * 100;
				const roundedPercentage = Math.round(percentageOfTotal * 100) / 100;

				tooltipItem.value = `${roundedPercentage}%`;
				return tooltipItem.value;
			}
		};
		options.legend!.reverse = this.renderSeriesInReverse;

		return options;
	}

	public mapData(data: Data): Chart.ChartData {
		const backgroundPalette = ChartConfig.borderPalette(this.seriesRenderOrder);
		const borderPalette = ChartConfig.borderPalette(this.seriesRenderOrder);

		return {
			labels: data.scale.labels,
			datasets: this.orderedSeries.map(
				(series, index) =>
					({
						label: this.getSeriesConfig(series.name).displayName,
						data: this.seriesToCompareTo ? this.getAdjustedValues(series) : series.data,
						backgroundColor: backgroundPalette[index],
						borderColor: borderPalette[index],
						hoverBackgroundColor: ColourService.setAlpha(backgroundPalette[index], 0.4),
						borderWidth: 1
					} as Chart.ChartDataSets)
			)
		};
	}

	private calculatePercentageLabels(): Map<string, string> {
		const labels = new Map<string, string>();

		const compareToSeries = this.getSeries(this.seriesToCompareTo);
		const compareToTotal = compareToSeries.data.reduce((sum, value) => sum + value, 0);

		this.data.series.forEach((series) => {
			const total = series.data.reduce((sum, value) => sum + value, 0);
			const percentageOfTotal = compareToTotal === 0 ? 0 : (total / compareToTotal) * 100;
			const roundedPercentage = Math.round(percentageOfTotal * 100) / 100;
			labels.set(series.name, `${roundedPercentage.toString()}%`);
		});

		return labels;
	}

	private getAdjustedValues(seriesData: Series): number[] {
		return seriesData.data.map((dataPoint, index) => {
			let value = dataPoint;
			value -= this.getValueForAdjustment(seriesData, index);
			return value;
		});
	}

	private getValueForAdjustment(seriesData: Series, index: number) {
		const thisSeriesIndex = this.getIndexOfSeries(seriesData.name);
		const adjustmentValueIsInSeriesAfter = this.getIndexOfSeries(this.seriesToCompareTo) === 0;

		if (adjustmentValueIsInSeriesAfter) {
			if (thisSeriesIndex < this.seriesRenderOrder.length) {
				return this.orderedSeries[thisSeriesIndex + 1].data[index];
			}
		} else if (thisSeriesIndex > 0) {
			return this.orderedSeries[thisSeriesIndex - 1].data[index];
		}

		return 0;
	}

	private get orderedSeries(): Series[] {
		return this.seriesRenderOrder.map(seriesConfig => this.getSeries(seriesConfig.name));
	}

	private getSeries(name: string): Series {
		return this.data.series.filter(series => series.name === name)[0];
	}

	private getSeriesConfig(name: string): SeriesConfig {
		return this.seriesConfig.filter(config => config.name === name)[0];
	}

	private get seriesConfig(): SeriesConfig[] {
		if (typeof this.seriesOrder[0] === 'string') {
			return (this.seriesOrder as string[]).map(seriesName => ({ name: seriesName, displayName: seriesName }));
		}
		return this.seriesOrder as SeriesConfig[];
	}

	private get seriesRenderOrder(): SeriesConfig[] {
		return this.renderSeriesInReverse ? this.seriesConfig.slice().reverse() : this.seriesConfig;
	}

	private getIndexOfSeries(name: string): number {
		return this.seriesRenderOrder.map(cfg => cfg.name).indexOf(name);
	}
}
